import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { usersApi } from '@services/api';
import { LoadingSpinner, EmptyState } from '@components/common';
import { formatDate } from '@utils';
import type { AdminUser, CreateUserInput, UpdateUserInput } from '@types';

const inputClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700';

/** Owner-only user administration (F1.3/F1.7). */
export default function UsersPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateUserInput>({
    email: '',
    name: '',
    password: '',
    role: 'staff',
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(input),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      setForm({ email: '', name: '', password: '', role: 'staff' });
      toast.success('User created');
    },
    onError: () => toast.error('Could not create user'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      usersApi.update(id, input),
    onSuccess: () => {
      invalidate();
      toast.success('User updated');
    },
    onError: () => toast.error('Could not update user'),
  });

  if (isLoading) {
    return (
      <div data-testid="users-loading" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="users-error">
        <EmptyState
          title="Couldn't load users"
          description="Something went wrong. Please try again."
          action={{ label: 'Retry', onClick: () => refetch() }}
        />
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <button
          type="button"
          data-testid="user-new-btn"
          onClick={() => setCreateOpen(true)}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          + New user
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((u: AdminUser) => (
              <tr key={u.id} data-testid="user-row" data-user-id={u.id}>
                <td className="px-4 py-3 text-gray-900">{u.name ?? '—'}</td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.role === 'owner'
                        ? 'bg-indigo-100 text-indigo-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">
                  {u.created_at ? formatDate(u.created_at) : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {u.role === 'staff' ? (
                      <button
                        type="button"
                        data-testid="user-promote"
                        onClick={() =>
                          updateMutation.mutate({
                            id: u.id,
                            input: { role: 'owner' },
                          })
                        }
                        className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                      >
                        Promote
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid="user-demote"
                        onClick={() =>
                          updateMutation.mutate({
                            id: u.id,
                            input: { role: 'staff' },
                          })
                        }
                        className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                      >
                        Demote
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="user-toggle-active"
                      onClick={() =>
                        updateMutation.mutate({
                          id: u.id,
                          input: { is_active: !u.is_active },
                        })
                      }
                      className={`rounded border px-2 py-1 text-xs ${
                        u.is_active
                          ? 'border-red-200 text-red-600 hover:bg-red-50'
                          : 'border-green-200 text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create-user dialog */}
      <Transition appear show={createOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setCreateOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel
                data-testid="user-create-dialog"
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
              >
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  New user
                </Dialog.Title>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      data-testid="user-email-input"
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={e =>
                        setForm(f => ({ ...f, email: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Name</label>
                    <input
                      data-testid="user-name-input"
                      className={inputClass}
                      value={form.name}
                      onChange={e =>
                        setForm(f => ({ ...f, name: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Temporary password</label>
                    <input
                      data-testid="user-password-input"
                      type="text"
                      className={inputClass}
                      value={form.password}
                      onChange={e =>
                        setForm(f => ({ ...f, password: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Role</label>
                    <select
                      data-testid="user-role-input"
                      className={inputClass}
                      value={form.role}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          role: e.target.value as 'owner' | 'staff',
                        }))
                      }
                    >
                      <option value="staff">Staff</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateOpen(false)}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="user-create-submit"
                    disabled={
                      !form.email.trim() ||
                      !form.password.trim() ||
                      createMutation.isPending
                    }
                    onClick={() => createMutation.mutate(form)}
                    className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating…' : 'Create user'}
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
